import { Empty, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { inventoryApi, type StoreSale } from '@/api/inventory';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function StoreOutstandingTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'store', 'sales', 'outstanding'],
    queryFn: () => inventoryApi.outstandingSales(),
  });

  const total = (data ?? []).reduce((s, r) => s + Number(r.outstanding), 0);

  const columns: TableProps<StoreSale>['columns'] = [
    {
      title: 'Receipt',
      dataIndex: 'receiptNumber',
      width: 100,
      render: (n: number) => (
        <Tag color="processing" style={{ fontFamily: 'monospace' }}>#{n}</Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'saleDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Student',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.studentName ?? row.walkInName}</Typography.Text>
          {row.studentRegistrationNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.studentRegistrationNumber}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Net',
      dataIndex: 'netAmount',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Paid',
      dataIndex: 'paidAmount',
      width: 130,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Outstanding',
      dataIndex: 'outstanding',
      width: 150,
      align: 'right',
      render: (v: string) => (
        <Tag color="warning" style={{ fontWeight: 600 }}>
          PKR {PKR(v)}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {data && data.length > 0 && (
        <Tag color="warning" style={{ fontSize: 13, alignSelf: 'flex-start' }}>
          Total outstanding · PKR {PKR(total)}
        </Tag>
      )}
      {data && data.length === 0 && !isLoading ? (
        <Empty description="No outstanding store credit" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Open a row from the Sales tab to record collection against it.
      </Typography.Text>
    </div>
  );
}
